import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  adminListPackages, adminCreatePackage, adminUpdatePackage, adminDeletePackage, adminListProducts, apiErr,
} from "@/lib/api";

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-charcoal/60">{label}</Label>
    {children}
  </div>
);
const selectCls = "h-10 w-full rounded-sm border border-input bg-background px-3 text-sm";
const slugify = (s) =>
  s.toLocaleLowerCase("tr").replace(/ı/g, "i").replace(/ş/g, "s").replace(/ğ/g, "g")
    .replace(/ü/g, "u").replace(/ö/g, "o").replace(/ç/g, "c").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function PackageEditDialog({ state, products, onClose, onSaved }) {
  const isCreate = state.mode === "create";
  const p = state.pkg || {};
  const [form, setForm] = useState({
    slug: p.slug || "", name: p.name || "", serves: p.serves || "", description: p.description || "",
    image: p.image || "", items: (p.items || []).map((it) => ({ ...it })),
  });
  const [pick, setPick] = useState({ slug: products[0]?.slug || "", quantity: "1 kg" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addItem = () => {
    const prod = products.find((x) => x.slug === pick.slug);
    if (!prod) return;
    setForm((f) => {
      if (f.items.some((i) => i.slug === prod.slug)) return f;
      return { ...f, items: [...f.items, { slug: prod.slug, name: prod.name, quantity: pick.quantity || "1 kg" }] };
    });
  };
  const rmItem = (slug) => setForm((f) => ({ ...f, items: f.items.filter((i) => i.slug !== slug) }));
  const setItemQty = (slug, q) => setForm((f) => ({ ...f, items: f.items.map((i) => (i.slug === slug ? { ...i, quantity: q } : i)) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Paket adı gerekli"); return; }
    if (form.items.length === 0) { toast.error("En az bir ürün ekleyin"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(), serves: form.serves, description: form.description,
        image: form.image || `/products/${form.items[0].slug}.webp`, items: form.items,
      };
      if (isCreate) { await adminCreatePackage({ slug: slugify(form.name), ...body }); toast.success("Paket oluşturuldu"); }
      else { await adminUpdatePackage(p.slug, { slug: p.slug, ...body }); toast.success("Paket güncellendi"); }
      onSaved();
    } catch (e) {
      toast.error(apiErr(e, "Kaydedilemedi"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isCreate ? "Yeni Paket" : `Düzenle · ${p.name}`}</DialogTitle>
          <DialogDescription className="font-mono text-xs text-charcoal/50">Paket adını, kaç kişilik olduğunu ve içindeki ürünleri düzenleyin.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2 sm:grid-cols-2">
          <Field label="Paket Adı"><Input data-testid="package-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Kaç Kişilik"><Input value={form.serves} onChange={(e) => set("serves", e.target.value)} placeholder="4-5 Kişilik" /></Field>
          <Field label="Açıklama" full><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} /></Field>
          <Field label="Kapak Görseli" full>
            <select className={selectCls} value={form.image} onChange={(e) => set("image", e.target.value)}>
              <option value="">— Otomatik (ilk ürün görseli) —</option>
              {products.map((pr) => <option key={pr.slug} value={pr.image}>{pr.name}</option>)}
            </select>
          </Field>
        </div>

        <div className="mt-2 border-t border-dashed border-charcoal/15 pt-4">
          <span className="mb-2 block font-mono text-[11px] uppercase tracking-wider text-charcoal/60">Paket İçeriği</span>
          <div className="mb-3 flex items-end gap-2">
            <select className={selectCls + " flex-1"} value={pick.slug} onChange={(e) => setPick((x) => ({ ...x, slug: e.target.value }))} data-testid="package-item-select">
              {products.map((pr) => <option key={pr.slug} value={pr.slug}>{pr.name}</option>)}
            </select>
            <Input className="w-28" value={pick.quantity} onChange={(e) => setPick((x) => ({ ...x, quantity: e.target.value }))} placeholder="1 kg" />
            <Button variant="outline" onClick={addItem} data-testid="package-item-add"><Plus className="h-4 w-4" /></Button>
          </div>
          <ul className="space-y-1.5">
            {form.items.map((it) => (
              <li key={it.slug} className="flex items-center gap-2 rounded-sm bg-[#faf7f2] px-3 py-2">
                <span className="flex-1 font-serif text-base text-charcoal">{it.name}</span>
                <Input className="h-8 w-24" value={it.quantity} onChange={(e) => setItemQty(it.slug, e.target.value)} />
                <button onClick={() => rmItem(it.slug)} className="text-charcoal/50 hover:text-red-600"><X className="h-4 w-4" /></button>
              </li>
            ))}
            {form.items.length === 0 && <li className="py-3 text-center font-mono text-xs text-charcoal/40">Henüz ürün eklenmedi</li>}
          </ul>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button data-testid="package-save-btn" onClick={save} disabled={saving} className="bg-bordo text-cream hover:bg-bordo-dark">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PackageManager() {
  const qc = useQueryClient();
  const { data: packages = [] } = useQuery({ queryKey: ["admin", "packages"], queryFn: adminListPackages, retry: false });
  const { data: products = [] } = useQuery({ queryKey: ["admin", "products"], queryFn: adminListProducts, retry: false });
  const [editing, setEditing] = useState(null);
  const reload = () => qc.invalidateQueries({ queryKey: ["admin", "packages"] });

  const remove = async (pkg) => {
    if (!window.confirm(`"${pkg.name}" paketini silmek istiyor musun?`)) return;
    try { await adminDeletePackage(pkg.slug); toast.success("Paket silindi"); reload(); }
    catch { toast.error("Silinemedi"); }
  };

  return (
    <div data-testid="package-manager" className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="new-package-btn" onClick={() => setEditing({ mode: "create" })} className="bg-bordo text-cream hover:bg-bordo-dark">
          <Plus className="mr-1 h-4 w-4" /> Yeni Paket
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {packages.map((pkg) => (
          <div key={pkg.slug} data-testid={`admin-package-${pkg.slug}`} className="border border-charcoal/10 bg-white">
            <img src={pkg.image} alt={pkg.name} className="h-32 w-full object-cover" />
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-serif text-xl text-charcoal">{pkg.name}</h3>
                <div className="flex gap-1">
                  <button onClick={() => setEditing({ mode: "edit", pkg })} data-testid={`edit-package-${pkg.slug}`} className="rounded p-1.5 text-charcoal/60 hover:bg-charcoal/5"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(pkg)} data-testid={`delete-package-${pkg.slug}`} className="rounded p-1.5 text-charcoal/60 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-bordo">{pkg.serves}</p>
              <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                {(pkg.items || []).map((it) => (
                  <li key={it.slug} className="font-mono text-xs text-charcoal/60">{it.name} · {it.quantity}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <PackageEditDialog
          state={editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}
